import CircularProgress from "@mui/material/CircularProgress";
import { useAudioPlayer } from "./AudioPlayerContext";
import "./bottomplayer.css";

// ─── Helpers ─────────────────────────────────────────────────────────────────
function formatTime(sec) {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

const FIXED_WAVEFORM = [
  0.25, 0.35, 0.3, 0.45, 0.4, 0.35, 0.5, 0.55, 0.45, 0.6, 0.7, 0.55, 0.8, 0.9,
  0.75, 0.95, 1.0, 0.95, 0.75, 0.9, 0.8, 0.55, 0.7, 0.6, 0.45, 0.55, 0.5, 0.35,
  0.4, 0.45, 0.3, 0.35, 0.25,
];

const SPEED_OPTIONS = [0.75, 1, 1.25, 1.5, 1.75, 2];

// Mini waveform that fills with orange up to the current playback position.
const WAVEFORM_PLAYED_COLOR = "#f47f22";
function WaveformMini({ progress = 0 }) {
  return (
    <div className="bp-waveform">
      {FIXED_WAVEFORM.map((h, i) => {
        const played = i / FIXED_WAVEFORM.length < progress;
        return (
          <div
            key={i}
            className="bp-waveform-bar"
            style={{
              height: `${h * 65}%`,
              ...(played ? { backgroundColor: WAVEFORM_PLAYED_COLOR } : null),
            }}
          />
        );
      })}
    </div>
  );
}

function PlayIcon({ size = 24 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="#3d3428"
      stroke="none"
    >
      <polygon points="6,3 20,12 6,21" />
    </svg>
  );
}

function PauseIcon({ size = 24 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="#3d3428"
      stroke="none"
    >
      <rect x="5" y="3" width="5" height="18" rx="1" />
      <rect x="14" y="3" width="5" height="18" rx="1" />
    </svg>
  );
}

function SkipBackIcon({ size = 18 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="#3d3428"
      stroke="none"
    >
      <polygon points="11,5 1,12 11,19" />
      <polygon points="22,5 12,12 22,19" />
    </svg>
  );
}

function SkipForwardIcon({ size = 18 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="#3d3428"
      stroke="none"
    >
      <polygon points="2,5 12,12 2,19" />
      <polygon points="13,5 23,12 13,19" />
    </svg>
  );
}

function VolumeIcon({ muted, size = 16 }) {
  if (muted) {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="#5a4a3a"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" fill="#5a4a3a" />
        <line x1="23" y1="9" x2="17" y2="15" />
        <line x1="17" y1="9" x2="23" y2="15" />
      </svg>
    );
  }
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="#5a4a3a"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" fill="#5a4a3a" />
      <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
    </svg>
  );
}

function CloseIconSvg({ size = 18 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

// ─── Bottom Player (reads from shared context) ───────────────────────────────
export function BottomPlayer({ lecture, onClose }) {
  const {
    playing,
    currentTime,
    duration,
    progress,
    volume,
    muted,
    loading,
    streamError,
    playbackRate = 1,
    togglePlay,
    skip,
    seek,
    setVolume,
    setMuted,
    setPlaybackRate,
  } = useAudioPlayer();

  const handleSeek = (e) => {
    seek(parseFloat(e.target.value));
  };

  const cycleSpeed = () => {
    if (!setPlaybackRate) return;
    const currentIdx = SPEED_OPTIONS.indexOf(playbackRate);
    const nextIdx =
      (currentIdx === -1 ? 1 : currentIdx + 1) % SPEED_OPTIONS.length;
    setPlaybackRate(SPEED_OPTIONS[nextIdx]);
  };

  // Format label: "1x" instead of "1.00x", "1.5x" instead of "1.50x"
  const speedLabel = `${Number.isInteger(playbackRate) ? playbackRate : playbackRate}x`;
  const speedActive = playbackRate !== 1;

  return (
    <div className="bp">
      <div className="bp-inner">
        <div className="bp-left">
          <div className="bp-thumb">
            <WaveformMini progress={progress} />
          </div>
          <div className="bp-info">
            {lecture.titleUr && (
              <div className="bp-title-ur" lang="ur" dir="rtl">
                {lecture.titleUr}
              </div>
            )}
            <div
              className={`bp-title-en${lecture.titleUr ? "" : " bp-title-en--solo"}`}
            >
              {loading
                ? "Loading..."
                : streamError
                  ? "Failed to load audio"
                  : lecture.titleEn || lecture.title || lecture.titleUr}
            </div>
          </div>
        </div>
        <div className="bp-center">
          <div className="bp-controls">
            <button className="bp-ctrl-btn" onClick={() => skip(-1)}>
              <SkipBackIcon />
            </button>
            <button
              className="bp-ctrl-btn bp-play-btn"
              onClick={togglePlay}
              disabled={loading || streamError}
            >
              {loading ? (
                <CircularProgress size={18} sx={{ color: "#3d3428" }} />
              ) : playing ? (
                <PauseIcon size={22} />
              ) : (
                <PlayIcon size={22} />
              )}
            </button>
            <button className="bp-ctrl-btn" onClick={() => skip(1)}>
              <SkipForwardIcon />
            </button>
          </div>
          <div className="bp-seek-row">
            <span className="bp-time">{formatTime(currentTime)}</span>
            <input
              type="range"
              className="bp-seek"
              min={0}
              max={1}
              step={0.001}
              value={progress}
              onChange={handleSeek}
              style={{
                background: `linear-gradient(to right, #5a4a3a 0%, #5a4a3a ${progress * 100}%, #d6d0c7 ${progress * 100}%, #d6d0c7 100%)`,
              }}
            />
            <span className="bp-time">{formatTime(duration)}</span>
          </div>
        </div>
        <div className="bp-right">
          <button
            className={`bp-ctrl-btn bp-speed-btn${speedActive ? " bp-speed-btn--active" : ""}`}
            onClick={cycleSpeed}
            title="Playback speed"
          >
            {speedLabel}
          </button>
          <button className="bp-ctrl-btn" onClick={() => setMuted(!muted)}>
            <VolumeIcon muted={muted} />
          </button>
          <input
            type="range"
            className="bp-volume"
            min={0}
            max={1}
            step={0.01}
            value={muted ? 0 : volume}
            onChange={(e) => {
              setVolume(parseFloat(e.target.value));
              setMuted(false);
            }}
            style={{
              background: `linear-gradient(to right, #5a4a3a 0%, #5a4a3a ${(muted ? 0 : volume) * 100}%, #d6d0c7 ${(muted ? 0 : volume) * 100}%, #d6d0c7 100%)`,
            }}
          />
          <button className="bp-ctrl-btn bp-close-btn" onClick={onClose}>
            <CloseIconSvg size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
